import { useEffect, useMemo, useState } from "react";
import { ref, onValue, set, update } from "firebase/database";
import { database } from "../constants/firebase";

const PollMessage = ({
    communityId,
    pollId,
    currentUser,
    isDark = true,
    isHost,
}) => {
    const [poll, setPoll] = useState(null);
    const [votes, setVotes] = useState({});
    const uid = currentUser?.uid;

    // Subscribe to poll + votes
    useEffect(() => {
        const pRef = ref(database, `polls/${communityId}/${pollId}`);
        const unsub1 = onValue(pRef, (snap) => setPoll(snap.val() || null));

        const vRef = ref(database, `pollVotes/${communityId}/${pollId}`);
        const unsub2 = onValue(vRef, (snap) => setVotes(snap.val() || {}));

        return () => {
            unsub1();
            unsub2();
        };
    }, [communityId, pollId]);

    const options = useMemo(() => {
        if (!poll?.options) return [];

        // ✅ NEW FORMAT (array)
        if (Array.isArray(poll.options)) {
            return [...poll.options]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(o => ({
                    optionId: o.id,
                    text: o.text,
                }));
        }

        // ✅ OLD FORMAT (object)
        if (typeof poll.options === "object") {
            return Object.entries(poll.options).map(([optionId, o], index) => ({
                optionId,
                text: o.text,
                order: index, // fallback order
            }));
        }

        return [];
    }, [poll]);


    // Compute vote counts
    const { counts, total } = useMemo(() => {
        const c = {};
        for (const opt of options) c[opt.optionId] = 0;
        Object.values(votes).forEach((v) => {
            if (typeof v === "string") {
                if (c[v] !== undefined) c[v] += 1;
            } else if (v && typeof v === "object") {
                Object.keys(v).forEach((k) => {
                    if (c[k] !== undefined) c[k] += 1;
                });
            }
        });
        const t = Object.values(c).reduce((a, b) => a + b, 0);
        return { counts: c, total: t };
    }, [votes, options]);

    const myVote = uid ? votes[uid] : null;
    const hasVoted = !!myVote;
    const isClosed = poll?.closed;

    // Submit vote immediately when option clicked
    const handleVote = async (optionId) => {
        if (!uid || isClosed) return;
        const vRef = ref(database, `pollVotes/${communityId}/${pollId}/${uid}`);

        if (poll.allowMultiple) {
            // --- MULTI SELECT LOGIC ---
            const currentVotes = (votes[uid] && typeof votes[uid] === "object") ? { ...votes[uid] } : {};
            if (currentVotes[optionId]) {
                // Unselect (remove)
                delete currentVotes[optionId];
            } else {
                // Select (add)
                currentVotes[optionId] = true;
            }
            await set(vRef, currentVotes);
        } else {
            // --- SINGLE SELECT LOGIC ---
            if (myVote === optionId) {
                // Unselect if same option clicked
                await set(vRef, null);
            } else {
                // Change to another option
                await set(vRef, optionId);
            }
        }
    };


    // Close poll (host only)
    const handleClosePoll = async () => {
        if (!isHost) return;
        const pRef = ref(database, `polls/${communityId}/${pollId}`);
        await update(pRef, { closed: true });
    };

    if (!poll) return null;

    const barBg = isDark ? "bg-[#2A2A2A]" : "bg-gray-100";
    const barFill = "bg-primary";

    return (
        <div className="md:max-w-[350px] md:min-w-[350px] max-w-[250px] min-w-[250px] w-full rounded-2xl p-4 {} bg-[#111111]/60 border border-white/10 backdrop-blur">
            <div className="mb-4 text-base font-semibold">{poll.question}</div>

            <div className="space-y-3">
                {options.map(({ optionId, text }) => {
                    const pct = total ? Math.round((counts[optionId] / total) * 100) : 0;
                    const checked = poll.allowMultiple
                        ? !!(myVote && typeof myVote === "object" && myVote[optionId])
                        : myVote === optionId;

                    const canInteract = !isClosed;

                    return (
                        <div
                            key={optionId}
                            onClick={() => canInteract && handleVote(optionId)}
                            className={`rounded-xl p-3 z-50 bg-[#1E1E1E] ${canInteract
                                ? "hover:bg-[#2A2A2A] cursor-pointer"
                                : "opacity-50 cursor-not-allowed"
                                }`}
                        >
                            {/* Progress bar */}
                            <div
                                className={`relative h-2 w-full ${barBg} rounded-full overflow-hidden pointer-events-none`}
                            >
                                <div
                                    className={`absolute left-0 top-0 h-2 ${barFill}`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>

                            {/* Option text */}
                            <div className="mt-2 flex items-center justify-between pointer-events-none">
                                <div className="flex items-center space-x-2">
                                    {poll.allowMultiple ? (
                                        <input
                                            type="checkbox"
                                            disabled
                                            checked={checked}
                                            readOnly
                                            className="h-4 w-4 accent-primary"
                                        />
                                    ) : (
                                        <input
                                            type="radio"
                                            disabled
                                            checked={checked}
                                            readOnly
                                            className="h-4 w-4 accent-primary"
                                        />
                                    )}
                                    <span className="text-sm">{text}</span>
                                </div>
                                <span className="text-xs text-white/60">{counts[optionId]}</span>
                            </div>
                        </div>

                    );
                })}
            </div>

            {/* Vote count */}
            <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-white/50">
                    {total} vote{total === 1 ? "" : "s"}
                </div>
            </div>

            {/* Close poll button (host only) */}
            {isHost && (
                <button
                    onClick={handleClosePoll}
                    disabled={isClosed}
                    className={`mt-3 w-full py-2 rounded-lg text-sm font-medium ${isClosed
                        ? "bg-gray-500 cursor-not-allowed opacity-60"
                        : "bg-red-500 hover:bg-red-600"
                        }`}
                >
                    {isClosed ? "Closed" : "Close Poll"}
                </button>
            )}
        </div>
    );
};

export default PollMessage;
